import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DynamicVisualizationChart = ({ 
  data = [], 
  title = "Análise de Performance",
  height = 400,
  showControls = true 
}) => {
  const [chartType, setChartType] = useState('line');
  const [selectedMetrics, setSelectedMetrics] = useState(['revenue', 'expenses']);

  // Mock data for comprehensive business intelligence
  const mockData = [
    {
      month: 'Jan',
      revenue: 45000,
      expenses: 32000,
      profit: 13000,
      customers: 1250,
      orders: 890,
      avgOrderValue: 50.56,
      marketShare: 12.5,
      efficiency: 78
    },
    {
      month: 'Fev',
      revenue: 52000,
      expenses: 35000,
      profit: 17000,
      customers: 1380,
      orders: 1020,
      avgOrderValue: 50.98,
      marketShare: 13.2,
      efficiency: 82
    },
    {
      month: 'Mar',
      revenue: 48000,
      expenses: 33000,
      profit: 15000,
      customers: 1320,
      orders: 960,
      avgOrderValue: 50.00,
      marketShare: 12.8,
      efficiency: 80
    },
    {
      month: 'Abr',
      revenue: 58000,
      expenses: 38000,
      profit: 20000,
      customers: 1450,
      orders: 1160,
      avgOrderValue: 50.00,
      marketShare: 14.1,
      efficiency: 85
    },
    {
      month: 'Mai',
      revenue: 62000,
      expenses: 40000,
      profit: 22000,
      customers: 1520,
      orders: 1240,
      avgOrderValue: 50.00,
      marketShare: 14.8,
      efficiency: 87
    },
    {
      month: 'Jun',
      revenue: 67000,
      expenses: 42000,
      profit: 25000,
      customers: 1680,
      orders: 1340,
      avgOrderValue: 50.00,
      marketShare: 15.5,
      efficiency: 89
    }
  ];

  const chartData = data?.length > 0 ? data : mockData;

  const chartTypes = [
    { key: 'line', label: 'Linha', icon: 'TrendingUp' },
    { key: 'bar', label: 'Barras', icon: 'BarChart3' },
    { key: 'area', label: 'Área', icon: 'Activity' },
    { key: 'scatter', label: 'Dispersão', icon: 'Scatter' },
    { key: 'composed', label: 'Combinado', icon: 'Layers' }
  ];

  const availableMetrics = [
    { key: 'revenue', label: 'Receita', color: '#F29F05' },
    { key: 'expenses', label: 'Despesas', color: '#3E241A' },
    { key: 'profit', label: 'Lucro', color: '#2E7D32' },
    { key: 'customers', label: 'Clientes', color: '#1976D2' },
    { key: 'orders', label: 'Pedidos', color: '#7B1FA2' },
    { key: 'efficiency', label: 'Eficiência (%)', color: '#F57C00' }
  ];

  const toggleMetric = (metricKey) => {
    setSelectedMetrics(prev => 
      prev?.includes(metricKey) 
        ? prev?.filter(m => m !== metricKey)
        : [...prev, metricKey]
    );
  };

  const formatValue = (value, key) => {
    if (key === 'revenue' || key === 'expenses' || key === 'profit') {
      return `R$ ${(value / 1000)?.toFixed(0)}k`;
    }
    if (key === 'efficiency' || key === 'marketShare') {
      return `${value}%`;
    }
    if (key === 'avgOrderValue') {
      return `R$ ${value?.toFixed(2)}`;
    }
    return value?.toLocaleString('pt-BR');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-modal">
          <p className="font-body-medium text-sm text-popover-foreground mb-2">
            {label}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry?.color }}
                />
                <span className="text-xs text-muted-foreground">
                  {entry?.name}:
                </span>
              </div>
              <span className="text-xs font-body-medium text-popover-foreground">
                {formatValue(entry?.value, entry?.dataKey)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E2" />
            <XAxis dataKey="month" stroke="#6B4E3D" fontSize={12} />
            <YAxis stroke="#6B4E3D" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {selectedMetrics?.map(metric => {
              const metricConfig = availableMetrics?.find(m => m?.key === metric);
              return (
                <Bar 
                  key={metric}
                  dataKey={metric} 
                  name={metricConfig?.label}
                  fill={metricConfig?.color} 
                />
              );
            })}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E2" />
            <XAxis dataKey="month" stroke="#6B4E3D" fontSize={12} />
            <YAxis stroke="#6B4E3D" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {selectedMetrics?.map(metric => {
              const metricConfig = availableMetrics?.find(m => m?.key === metric);
              return (
                <Area 
                  key={metric}
                  type="monotone"
                  dataKey={metric} 
                  name={metricConfig?.label}
                  stroke={metricConfig?.color}
                  fill={metricConfig?.color}
                  fillOpacity={0.3}
                />
              );
            })}
          </AreaChart>
        );

      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E2" />
            <XAxis dataKey="customers" name="Clientes" stroke="#6B4E3D" fontSize={12} />
            <YAxis dataKey="revenue" name="Receita" stroke="#6B4E3D" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Scatter name="Receita vs Clientes" data={chartData} fill="#F29F05" />
          </ScatterChart>
        );

      case 'composed':
        return (
          <ComposedChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E2" />
            <XAxis dataKey="month" stroke="#6B4E3D" fontSize={12} />
            <YAxis stroke="#6B4E3D" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="revenue" name="Receita" fill="#F29F05" />
            <Line type="monotone" dataKey="efficiency" name="Eficiência (%)" stroke="#2E7D32" strokeWidth={3} />
          </ComposedChart>
        );

      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EAE6E2" />
            <XAxis dataKey="month" stroke="#6B4E3D" fontSize={12} />
            <YAxis stroke="#6B4E3D" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {selectedMetrics?.map(metric => {
              const metricConfig = availableMetrics?.find(m => m?.key === metric);
              return (
                <Line 
                  key={metric}
                  type="monotone" 
                  dataKey={metric} 
                  name={metricConfig?.label}
                  stroke={metricConfig?.color} 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              );
            })}
          </LineChart>
        );
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading-semibold text-lg text-card-foreground">
          {title}
        </h3>
        
        {showControls && (
          <div className="flex items-center space-x-2">
            {chartTypes?.map(type => (
              <Button
                key={type?.key}
                variant={chartType === type?.key ? "default" : "outline"}
                size="sm"
                iconName={type?.icon}
                onClick={() => setChartType(type?.key)}
              >
                {type?.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      {showControls && chartType !== 'scatter' && chartType !== 'composed' && (
        <div className="mb-4 p-4 bg-muted rounded-lg">
          <p className="text-sm font-body-medium text-muted-foreground mb-2">
            Métricas Selecionadas:
          </p>
          <div className="flex flex-wrap gap-2">
            {availableMetrics?.map(metric => (
              <button
                key={metric?.key}
                onClick={() => toggleMetric(metric?.key)}
                className={`
                  px-3 py-1 rounded-full text-xs font-body-medium transition-smooth
                  ${selectedMetrics?.includes(metric?.key)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-card-foreground border border-border hover:bg-accent'
                  }
                `}
              >
                <div className="flex items-center space-x-1">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: metric?.color }}
                  />
                  <span>{metric?.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          {renderChart()}
        </ResponsiveContainer>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Dados atualizados em: 29/08/2025 10:00</span>
          <div className="flex items-center space-x-4">
            <span>Intervalo de confiança: 95%</span>
            <div className="flex items-center space-x-1">
              <Icon name="TrendingUp" size={12} className="text-success" />
              <span>Tendência positiva detectada</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicVisualizationChart;